import { typography, radius } from '@/shared/theme/tokens';
import { REC_IMPACT_META, OPEN_LOOP_META } from '../../liveModeMeta';
import { BOARD } from './boardTheme';
import { SplitFlap } from './SplitFlap';
import { SectionHeader, Tag, Empty } from './BoardChrome';

const MONO = BOARD.mono;
const VISIBLE = 4; // a passive board stays glance-resumable: stable order, never a carousel.

const COLUMNS = [
  { label: 'Parameter', flex: 1.35 },
  { label: 'Set-point', flex: 1.5 },
  { label: 'Impact', flex: 1, align: 'right' },
];

/**
 * Recommendations departures board — a static, priority-ranked stack of the recs
 * still awaiting an operator decision (read-only; a row opens the page drawer).
 * Setpoint moves render as split-flap counters (current ──▶ recommended). When
 * the feed is degraded the advisory is paused and this board reads as suppressed.
 */
export function RecBoard({ recs = [], paused = false, animate, onSelect }) {
  const shown = recs.slice(0, VISIBLE);
  const overflow = recs.length - shown.length;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', background: BOARD.board }}>
      <SectionHeader title="Recommendations" count={paused ? undefined : recs.length} accent={BOARD.amber} columns={paused ? [] : COLUMNS} />
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {paused ? (
          <Empty icon="sensors_off" color={BOARD.cyan} text="Advisory Paused" sub="Live inputs are stale — recommendations are held until the feed recovers." />
        ) : shown.length === 0 ? (
          <Empty icon="check_circle" color={BOARD.ok} text="No Actions Waiting" sub="Nothing needs an operator decision right now." />
        ) : (
          <>
            {shown.map((rec, i) => (
              <RecRow key={rec.id} rec={rec} top={i === 0} animate={animate} onSelect={onSelect} />
            ))}
            {overflow > 0 && (
              <div style={{
                fontFamily: MONO, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold,
                color: BOARD.inkFaint, letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase',
                padding: '12px 16px', borderTop: `1px solid ${BOARD.seam}`, fontVariantNumeric: 'tabular-nums',
              }}>
                +{overflow} more in queue
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function RecRow({ rec, top, animate, onSelect }) {
  const impact = REC_IMPACT_META[rec.impact] ?? REC_IMPACT_META.low;
  const numerical = rec.action?.type === 'numerical';
  const openLoop = rec.status === 'accepted';
  const clickable = typeof onSelect === 'function';

  return (
    <div
      onClick={clickable ? () => onSelect(rec) : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(rec); } } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? `Open ${rec.parameterName} recommendation` : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
        borderBottom: `1px solid ${BOARD.seam}`,
        borderLeft: `3px solid ${top ? BOARD.amber : 'transparent'}`,
        background: top ? BOARD.chrome : 'transparent',
        cursor: clickable ? 'pointer' : 'default',
        animation: animate ? 'board-boot 0.3s ease' : 'none',
      }}
    >
      {/* PARAMETER */}
      <div style={{ flex: 1.35, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
        <span style={{
          fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize['3xl'],
          fontWeight: typography.fontWeight.extrabold, letterSpacing: typography.letterSpacing.wide,
          textTransform: 'uppercase', color: BOARD.ink,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {rec.parameterName}
        </span>
        {top && (
          <span style={{ fontFamily: MONO, fontSize: typography.fontSize.sm, color: BOARD.inkDim, lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {rec.expectedImpact}
          </span>
        )}
      </div>

      {/* SET-POINT */}
      <div style={{ flex: 1.5, minWidth: 0 }}>
        {numerical ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <SplitFlap value={rec.action.currentValue} color={BOARD.inkDim} size="md" animate={animate} dim />
            <span className="material-symbols-outlined" style={{ fontSize: 26, color: BOARD.amber }}>arrow_right_alt</span>
            <SplitFlap value={rec.action.recommendedValue} color={BOARD.amber} size="lg" animate={animate} />
            {rec.unit && (
              <span style={{ fontFamily: MONO, fontSize: typography.fontSize.md, color: BOARD.inkFaint }}>{rec.unit}</span>
            )}
          </div>
        ) : (
          <div style={{ fontFamily: typography.fontFamily.body, fontSize: typography.fontSize.lg, color: BOARD.ink, lineHeight: 1.4 }}>
            {rec.action?.instruction}
          </div>
        )}
      </div>

      {/* IMPACT */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, textAlign: 'right' }}>
        <Tag color={impact.color} solid={rec.impact === 'high'}>{impact.label} IMPACT</Tag>
        <span style={{
          fontFamily: MONO, fontSize: typography.fontSize.xs, color: BOARD.inkFaint,
          letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', fontVariantNumeric: 'tabular-nums',
        }}>
          Δ in {rec.timeToImpact}
        </span>
        {openLoop && (
          <span style={{
            fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
            letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', color: OPEN_LOOP_META.color,
            border: `1px solid ${OPEN_LOOP_META.color}`, borderRadius: radius.xs, padding: '2px 7px', textAlign: 'right',
          }}>
            Acknowledged — awaiting confirmation
          </span>
        )}
      </div>
    </div>
  );
}
